import React, { useEffect, useState } from "react";
import Tooltip from "@components/dashboard/tooltip";
import { fetchIntegrations, saveIntegration, testWriteback, revealPrevisionToken } from "./integrationsApi";
import PrevisionProjectsModal from "./PrevisionProjectsModal";

const WRITEBACK_AUTHORIZATIONS_MESSAGE = (
  <div style={{ color: "#fff", fontSize: "0.75rem", wordBreak: "break-all" }}>
    <p style={{ color: "#fff", marginBottom: 4 }}>
      Pedir pro cliente autorizar estes 4 endpoints no painel Sienge dele (Integrações → Gerenciamento de API):
    </p>
    <ul style={{ color: "#fff", margin: 0, paddingLeft: 14 }}>
      <li style={{ color: "#fff" }}>GET /bills/{"{billId}"}/buildings-cost</li>
      <li style={{ color: "#fff" }}>PUT /bills/{"{billId}"}/buildings-cost</li>
      <li style={{ color: "#fff" }}>GET /building-cost-estimations/{"{buildingId}"}/sheets</li>
      <li style={{ color: "#fff" }}>GET .../sheets/{"{buildingUnitId}"}/items</li>
    </ul>
  </div>
);

const INDEXED_BUDGET_MESSAGE = (
  <div style={{ color: "#fff", fontSize: "0.75rem" }}>
    <p style={{ color: "#fff", marginBottom: 4 }}>
      Como o "Orçamento Indexado" do cliente é calculado (coluna da tabela + cards):
    </p>
    <ul style={{ color: "#fff", margin: 0, paddingLeft: 14 }}>
      <li style={{ color: "#fff" }}>
        <b>Acumulativo (padrão)</b>: mês a mês, só o saldo em aberto (orçado − pago) recebe a
        inflação do índice; ao chegar em 100% medido, congela no acumulado.
      </li>
      <li style={{ color: "#fff" }}>
        <b>Global</b>: orçamento inteiro × variação do índice desde a data-base (metodologia
        Lumis).
      </li>
    </ul>
    <p style={{ color: "#fff", margin: "4px 0 0" }}>
      ⚠️ Trocar muda os números do cliente imediatamente.
    </p>
  </div>
);

const PREVISION_TOKEN_MESSAGE = (
  <div style={{ color: "#fff", fontSize: "0.75rem" }}>
    <p style={{ color: "#fff", marginBottom: 4 }}>
      Token da API do Prevision (GraphQL) — usado para importar a linha de base do cronograma.
    </p>
    <p style={{ color: "#fff", margin: 0 }}>
      Um admin do cliente gera em Configurações → Token da API na plataforma Prevision. Fica sempre
      mascarado; o valor completo só aparece ao clicar em Visualizar.
    </p>
  </div>
);

const writebackDiagnosticState = (result) => {
  if (!result) return null;
  const detail = typeof result.message === "string" ? result.message : "";
  const inferredReadsOk = /(?:^|·)\s*sheets:\s*200\b/i.test(detail)
    && /(?:^|·)\s*sheet-items:\s*200\b/i.test(detail)
    && /(?:^|·)\s*buildings-cost GET:\s*(?:200|404)\b/i.test(detail);
  const readsOk = result.readChecksOk === true
    || (result.readChecksOk === undefined && inferredReadsOk);
  if (!readsOk) return "fail";
  const putUnknown = result.putStatus === "unknown_until_controlled_write"
    || /PUT:\s*unknown_until_controlled_write/i.test(detail);
  if (putUnknown) return "unknown";
  const putProven = result.putStatus === 204
    || result.putStatus === "204"
    || result.putStatus === "verified"
    || /(?:^|·)\s*PUT:\s*204\b/i.test(detail);
  return putProven ? "ok" : "unknown";
};

// Write-back (reapropriação no ERP) é recurso Sienge. O back-end informa
// `writebackSupported`; sem o campo (back-end anterior), decide pelo ERP da empresa.
export const writebackSupportedFor = (client) =>
  client?.writebackSupported ?? String(client?.erp ?? "").trim().toUpperCase() === "SIENGE";

const IntegrationRow = ({ client, onConfigurePrevision }) => {
  const writebackSupported = writebackSupportedFor(client);
  const [apiLimitV1, setApiLimitV1] = useState(client.apiLimitV1 || "");
  const [notes, setNotes] = useState(client.integrationNotes || "");
  const [writebackEnabled, setWritebackEnabled] = useState(!!client.writebackEnabled);
  const [indexedBudgetMethod, setIndexedBudgetMethod] = useState(client.indexedBudgetMethod || "accrual");
  // Token da API do Prevision: a listagem só traz a forma mascarada; o valor completo
  // vem sob demanda (botão "visualizar") e some de novo ao ocultar.
  const [previsionTokenInput, setPrevisionTokenInput] = useState("");
  const [previsionTokenMasked, setPrevisionTokenMasked] = useState(client.previsionTokenMasked || null);
  const [revealedToken, setRevealedToken] = useState(null);
  const [revealing, setRevealing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(
    client.writebackTestedAt
      ? {
        ok: client.writebackTestOk,
        message: client.writebackTestDetail,
        testedAt: client.writebackTestedAt,
        readChecksOk: client.writebackReadChecksOk,
        putStatus: client.writebackPutStatus,
      }
      : null
  );
  const diagnosticState = writebackDiagnosticState(testResult);
  const diagnosticPrefix = {
    unknown: "⚠️ PUT ainda não testado — ",
    ok: "✅ ",
    fail: "❌ ",
  }[diagnosticState];

  // Alterações não persistidas: o checkbox/campos só gravam ao clicar em "Salvar" da linha.
  const dirty =
    apiLimitV1 !== (client.apiLimitV1 || "") ||
    notes !== (client.integrationNotes || "") ||
    writebackEnabled !== !!client.writebackEnabled ||
    indexedBudgetMethod !== (client.indexedBudgetMethod || "accrual") ||
    previsionTokenInput.trim() !== "";

  const onSave = async () => {
    setSaving(true);
    const newToken = previsionTokenInput.trim();
    const res = await saveIntegration(String(client._id), {
      apiLimitV1,
      notes,
      writebackEnabled,
      indexedBudgetMethod,
      ...(newToken ? { previsionToken: newToken } : {}),
    });
    setSaving(false);
    setSaved(!!res?.integration);
    if (res?.integration) {
      if (newToken) {
        setPrevisionTokenMasked(res.integration.previsionTokenMasked ?? previsionTokenMasked);
        setPrevisionTokenInput("");
        setRevealedToken(null);
      }
      setTimeout(() => setSaved(false), 2000);
    }
  };

  const onToggleReveal = async () => {
    if (revealedToken !== null) {
      setRevealedToken(null);
      return;
    }
    setRevealing(true);
    const res = await revealPrevisionToken(String(client._id));
    setRevealing(false);
    if (res?.token) setRevealedToken(res.token);
  };

  const onTest = async () => {
    setTesting(true);
    const res = await testWriteback(String(client._id));
    setTesting(false);
    setTestResult(res ?? { ok: false, message: "Erro ao testar" });
  };

  return (
    <tr>
      <td>
        <b>{client.name}</b>
        {client.subdomain ? <span className="integrations-sub">{client.subdomain}</span> : null}
      </td>
      <td>{client.erp || "—"}</td>
      <td className="num">{client.activeBuildings}</td>
      <td>{client.lastMeasurementDate || "—"}</td>
      <td>
        {client.purchaseOrders > 0 ? (
          <span className="integrations-chip integrations-chip--on">
            ✔ {client.purchaseOrders} pedidos{client.lastPurchaseOrderDate ? ` · até ${client.lastPurchaseOrderDate}` : ""}
          </span>
        ) : (
          <span className="integrations-chip">não sincado</span>
        )}
      </td>
      <td className="num">{client.auditReports || "—"}</td>
      <td className="num">{client.apiLimitBulk ?? "—"}</td>
      <td>
        <input
          type="text"
          value={apiLimitV1}
          placeholder="ex: 1000/dia"
          onChange={(e) => setApiLimitV1(e.target.value)}
        />
      </td>
      <td className="integrations-notes">
        <input
          type="text"
          value={notes}
          placeholder="Status / observações da integração"
          onChange={(e) => setNotes(e.target.value)}
        />
      </td>
      <td className="num">
        <input
          type="checkbox"
          aria-label={`Escrita no ERP de ${client.name}`}
          checked={writebackSupported && writebackEnabled}
          disabled={!writebackSupported}
          onChange={(e) => setWritebackEnabled(e.target.checked)}
          title={
            !writebackSupported
              ? "Reapropriação no ERP disponível só para construtoras Sienge"
              : dirty ? "Alteração não salva — clique em Salvar nesta linha" : undefined
          }
        />
        {writebackEnabled !== !!client.writebackEnabled && (
          <span title="Não salvo — clique em Salvar" style={{ color: "#d9822b", marginLeft: 4, fontWeight: 700 }}>
            *
          </span>
        )}
      </td>
      <td>
        <select
          value={indexedBudgetMethod}
          onChange={(e) => setIndexedBudgetMethod(e.target.value)}
          title={
            indexedBudgetMethod !== (client.indexedBudgetMethod || "accrual")
              ? "Não salvo — clique em Salvar nesta linha"
              : undefined
          }
        >
          <option value="accrual">Acumulativo (padrão)</option>
          <option value="global">Global (orçado × fator)</option>
        </select>
        {indexedBudgetMethod !== (client.indexedBudgetMethod || "accrual") && (
          <span title="Não salvo — clique em Salvar" style={{ color: "#d9822b", marginLeft: 4, fontWeight: 700 }}>
            *
          </span>
        )}
      </td>
      <td className="integrations-prevision-token">
        {previsionTokenMasked ? (
          <p style={{ whiteSpace: "nowrap", marginBottom: 4 }}>
            <code>{revealedToken ?? previsionTokenMasked}</code>
            <button
              type="button"
              disabled={revealing}
              onClick={onToggleReveal}
              style={{ marginLeft: 6 }}
              title={revealedToken !== null ? "Ocultar o token" : "Visualizar o token completo"}
            >
              {revealing ? "…" : revealedToken !== null ? "Ocultar" : "Visualizar"}
            </button>
          </p>
        ) : (
          <p style={{ color: "#888", marginBottom: 4 }}>sem token</p>
        )}
        <input
          type="password"
          autoComplete="new-password"
          value={previsionTokenInput}
          placeholder={previsionTokenMasked ? "substituir token…" : "colar token…"}
          onChange={(e) => setPrevisionTokenInput(e.target.value)}
          title={previsionTokenInput ? "Não salvo — clique em Salvar nesta linha" : undefined}
        />
      </td>
      <td>
        <button
          type="button"
          aria-label={`Configurar obras Prevision de ${client.name}`}
          onClick={() => onConfigurePrevision(client)}
        >
          Configurar obras
        </button>
      </td>
      <td>
        <button
          type="button"
          disabled={testing || !writebackSupported}
          title={writebackSupported ? undefined : "Teste de liberação do Sienge — não se aplica a esta construtora"}
          onClick={onTest}
        >
          {testing ? "Testando…" : "Testar liberação"}
        </button>
        {testResult && (
          <p
            className={`integrations-test-${diagnosticState}`}
            style={diagnosticState === "unknown" ? { color: "#b54708" } : undefined}
          >
            {diagnosticPrefix}
            {testResult.message}
            {testResult.testedAt && (
              <span className="integrations-test-date">
                {" "}
                · testado em {new Date(testResult.testedAt).toLocaleString("pt-BR")}
              </span>
            )}
          </p>
        )}
      </td>
      <td>
        <button
          type="button"
          disabled={saving}
          onClick={onSave}
          style={dirty && !saving ? { fontWeight: 700, outline: "2px solid #d9822b" } : undefined}
        >
          {saving ? "…" : saved ? "Salvo ✔" : "Salvar"}
        </button>
        {dirty && !saving && !saved && (
          <span style={{ color: "#d9822b", fontSize: 12, marginLeft: 6, whiteSpace: "nowrap" }}>
            • não salvo
          </span>
        )}
      </td>
    </tr>
  );
};

const Integrations = () => {
  const [clients, setClients] = useState(null);
  const [query, setQuery] = useState("");
  const [previsionClient, setPrevisionClient] = useState(null);

  useEffect(() => {
    fetchIntegrations().then((res) => setClients(res?.clients ?? []));
  }, []);

  const visible = (clients ?? []).filter(
    (c) => !query || c.name?.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className="integrations-page">
      <h2>Integrações de clientes</h2>
      <p className="margin-bottom-16">
        Ferramenta interna — visão por cliente do que a integração tem e onde estamos: dados vivos do banco
        (obras, medições, pedidos sincados, auditorias) e campos manuais (limite da API v1, status/notas,
        liberação do write-back Sienge).
      </p>
      <div className="integrations-controls">
        <input
          type="search"
          placeholder="Filtrar por cliente…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {clients === null ? (
        <p>Carregando…</p>
      ) : (
        <div className="integrations-scroll">
          <table className="integrations-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>ERP</th>
                <th className="num">Obras ativas</th>
                <th>Última medição</th>
                <th>Pedidos (sync)</th>
                <th className="num">Auditorias</th>
                <th className="num">Limite bulk</th>
                <th>Limite API v1</th>
                <th>Status / notas</th>
                <th className="num">
                  Liberação financeira <Tooltip message={WRITEBACK_AUTHORIZATIONS_MESSAGE} />
                </th>
                <th>
                  Orçamento Indexado <Tooltip message={INDEXED_BUDGET_MESSAGE} />
                </th>
                <th>
                  Token Prevision <Tooltip message={PREVISION_TOKEN_MESSAGE} />
                </th>
                <th>Obras Prevision</th>
                <th>Testar liberação</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <IntegrationRow
                  key={String(c._id)}
                  client={c}
                  onConfigurePrevision={setPrevisionClient}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {previsionClient ? (
        <PrevisionProjectsModal
          companyId={String(previsionClient._id)}
          companyName={previsionClient.name}
          onClose={() => setPrevisionClient(null)}
        />
      ) : null}
    </div>
  );
};

export default Integrations;
